import React, { useEffect, useRef, useState } from 'react';
import { MdDescription, MdClose, MdDownload, MdOpenInNew, MdSearch } from 'react-icons/md';
import { useLabels } from '../contexts/LanguageContext';
import { getActiveUserInfo } from '../utils/localStore.util';
import { usePageMeta } from '../utils/pageMeta.util';
import { useSetPageHeader } from '../contexts/PageHeaderContext';

interface Invoice {
  id: number;
  title: string;
  file: string;
  user_id: number;
  payment_status: number;
  created_at: string;
  bill_url: string;
}

interface ApiResponse {
  code: number;
  status: boolean;
  message: string;
  data: Invoice[];
}

const BillingPage: React.FC = () => {
  usePageMeta({
    title: 'Billing | HCAE Client Portal',
    description: 'View open and past invoices, preview bills, and download billing documents in the HCAE Client Portal.',
  });
  useSetPageHeader('Billing', 'Manage your invoices and payment history');

  const labels = useLabels();
  const user = getActiveUserInfo();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [activeTab, setActiveTab] = useState<'paid' | 'unpaid'>('unpaid');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<Invoice | null>(null);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 400);
  };

  useEffect(() => { fetchInvoices(activeTab, debouncedSearch); }, [activeTab, debouncedSearch]);
  useEffect(() => { setPreview(null); }, [debouncedSearch]);
  useEffect(() => { setPreviewLoaded(false); }, [preview?.id]);

  const handleDownload = async (url: string, title: string) => {
    try {
      const extension = url.split('.').pop()?.toLowerCase() || 'pdf';
      const filename = `${title}.${extension}`;
      const proxyUrl = `${BASE_URL}download-file?url=${encodeURIComponent(url)}`;

      const response = await fetch(proxyUrl, {
        headers: { token: `${user?.api_token}` },
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const fetchInvoices = async (status: 'paid' | 'unpaid', search = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ payment_status: status });
      if (search.trim()) params.set('search', search.trim());
      const response = await fetch(`${BASE_URL}bill/list?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', token: `${user?.api_token}` },
      });
      const data: ApiResponse = await response.json();
      setInvoices(data?.data || []);
    } catch (error) {
      console.error('Billing fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('en-GB').replace(/\//g, '-');

  const renderSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="bg-white rounded-2xl border border-brand-100 p-4 animate-pulse">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-brand-100 shrink-0" />
            <div className="flex-1">
              <div className="h-3.5 w-36 rounded-full bg-brand-100 mb-2" />
              <div className="h-2.5 w-20 rounded-full bg-slate-100" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">


      {/* Tabs + Search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex flex-wrap gap-2 bg-white border border-slate-100 rounded-2xl p-1 w-full sm:w-fit shadow-sm shrink-0">
          {(['unpaid', 'paid'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); setPreview(null); }}
              className={`px-4 sm:px-5 py-2 rounded-xl text-sm font-semibold transition-all duration-200 flex-1 sm:flex-none ${activeTab === tab
                ? 'bg-brand-700 text-white shadow-md shadow-brand-200'
                : 'text-slate-500 hover:text-brand-700 hover:bg-brand-50'
                }`}
            >
              {tab === 'unpaid' ? labels.lbl_open_invoice : labels.lbl_past_invoice}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:max-w-xs">
          <MdSearch size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search invoices…"
            className="w-full pl-9 pr-9 py-2 text-sm bg-white border border-slate-200 rounded-2xl shadow-sm placeholder-slate-400 text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-300 transition"
          />
          {searchQuery && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
            >
              <MdClose size={15} />
            </button>
          )}
        </div>
      </div>

      {/* Content with optional side preview */}
      {preview ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
          {/* Invoice list */}
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <button
                key={invoice.id}
                onClick={() => setPreview(invoice)}
                className={`w-full text-left flex items-center gap-3 p-4 rounded-2xl border transition-all duration-200 ${preview?.id === invoice.id
                  ? 'border-brand-200 bg-brand-50 shadow-md'
                  : 'border-brand-100 bg-white hover:border-brand-200 hover:shadow-sm'
                  }`}
              >
                <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600 shrink-0">
                  <MdDescription size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-800 truncate">{invoice.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{formatDate(invoice.created_at)}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${activeTab === 'unpaid' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                  {activeTab === 'unpaid' ? 'Open' : 'Paid'}
                </span>
              </button>
            ))}
          </div>

          {/* Preview panel */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-5 py-4 border-b border-slate-100">
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-800 text-sm truncate">{preview.title}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{formatDate(preview.created_at)}</p>
              </div>
              <div className="flex gap-2 sm:justify-end">
                <button
                  onClick={() => window.open((preview.bill_url || preview.file).trim(), '_blank', 'noopener,noreferrer')}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-white text-slate-600 text-xs font-semibold rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors flex-1 sm:flex-none"
                >
                  <MdOpenInNew size={16} />
                  Open
                </button>
                <button
                  onClick={() => handleDownload((preview.bill_url || preview.file).trim(), preview.title)}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-brand-700 text-white text-xs font-semibold rounded-xl hover:bg-brand-800 transition-colors flex-1 sm:flex-none"
                >
                  <MdDownload size={16} />
                  Download
                </button>
                <button
                  onClick={() => setPreview(null)}
                  className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors shrink-0"
                >
                  <MdClose size={18} />
                </button>
              </div>
            </div>
            {(preview.bill_url || preview.file) ? (
              <div className="relative bg-slate-50">
                {!previewLoaded && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center">
                    <div className="w-full h-full p-4 sm:p-6">
                      <div className="h-full w-full rounded-xl bg-white border border-slate-100 shadow-sm animate-pulse" />
                    </div>
                  </div>
                )}
                <iframe
                  src={`${(preview.bill_url || preview.file).trim()}#toolbar=0&navpanes=0&scrollbar=0`}
                  className="w-full"
                  style={{ height: 'min(70vh, calc(100vh - 360px))' }}
                  title={preview.title}
                  onLoad={() => setPreviewLoaded(true)}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center" style={{ height: 'min(70vh, calc(100vh - 360px))' }}>
                <p className="text-slate-400 text-sm">No preview available.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {loading ? (
            renderSkeleton()
          ) : invoices.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 sm:gap-4">
              {invoices.map((invoice) => (
                <button
                  key={invoice.id}
                  onClick={() => setPreview(invoice)}
                  className="w-full text-left bg-white rounded-2xl border border-brand-100 p-4 flex items-center gap-4 hover:shadow-md hover:border-brand-200 transition-all duration-200 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center text-brand-500 shrink-0 group-hover:bg-brand-100 transition-colors">
                    <MdDescription size={22} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-slate-800 truncate">{invoice.title}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{formatDate(invoice.created_at)}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0 ${activeTab === 'unpaid' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                    }`}>
                    {activeTab === 'unpaid' ? 'Open' : 'Paid'}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-100 flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-300 mb-4">
                <MdDescription size={30} />
              </div>
              <p className="text-slate-400 font-medium">No invoices found</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BillingPage;
